/**
 * Contractor Profile Page
 *
 * Public-facing individual contractor profile page.
 * URL: /contractors/:slug (marketing site: rooflyzer.pro/contractors/BRAND_NAME)
 * Non-indexable for now (noindex, nofollow).
 *
 * 2026-09-09 Rogue Move rebrand: the per-element `|| '#465fff'` fallback
 * color used throughout this file was updated to Rogue Move Purple
 * `#7950a2` so the marketing surface (chrome, links, accent nodes)
 * reads as the same brand as the main app. Tenant's own
 * `brandKit.primary_color` always wins over the fallback.
 */

import { SEOHead } from '@/components/seo/SEOHead';
import { getBrandKitBySlug, type PublicBrandKit } from '@/lib/brandKitApi';
import { getAppLink } from '@/lib/links';
import { Building2, Globe, Loader2, Mail, MapPin, Phone, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

const BRAND_PURPLE = '#7950a2';
const WCAG_AA_TEXT_CONTRAST = 4.5;

/** WCAG 2.x relative luminance of a 6-digit hex color. */
function relativeLuminance(hex: string): number {
  const lin = (offset: number) => {
    const channel = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
}

/**
 * First candidate brand color readable as text on the white profile
 * card (WCAG AA >= 4.5:1), falling back to Rogue Move Purple. Light
 * accent tones — e.g. the shared default yellow #f7c76c (~1.5:1 on
 * white) — are decorative only and must not color link text
 * (PR #1989 codeant review). Primary is checked first to preserve the
 * primary-wins priority merged in #1989.
 */
function accessibleTextOnWhite(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    const hex = candidate?.trim().toLowerCase();
    if (
      hex &&
      /^#[0-9a-f]{6}$/.test(hex) &&
      1.05 / (relativeLuminance(hex) + 0.05) >= WCAG_AA_TEXT_CONTRAST
    ) {
      return hex;
    }
  }
  return BRAND_PURPLE;
}

export function ContractorProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const [brandKit, setBrandKit] = useState<PublicBrandKit | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadBrandKit = async () => {
      if (!slug) {
        setError('Invalid contractor URL');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getBrandKitBySlug(slug);

        if (!data) {
          setError('Contractor profile not found or not public');
        } else {
          setBrandKit(data);
        }
      } catch (err: unknown) {
        console.error('Error loading contractor profile:', err);
        setError('Failed to load contractor profile');
      } finally {
        setLoading(false);
      }
    };

    loadBrandKit();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2
            className="size-12 animate-spin mx-auto mb-4"
            style={{ color: brandKit?.primary_color || '#7950a2' }}
          />
          <p className="text-gray-600">Loading contractor profile...</p>
        </div>
      </div>
    );
  }

  if (error || !brandKit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Contractor Not Found</h1>
          <p className="text-gray-600 mb-4">
            {error || 'This contractor profile does not exist or is not publicly available.'}
          </p>
          <Link to="/contractors" className="hover:underline" style={{ color: '#7950a2' }}>
            Browse All Contractors
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${brandKit.company_name} | Roofing Contractor`}
        description={
          brandKit.tagline || `Professional roofing services by ${brandKit.company_name}`
        }
        canonicalUrl={`/contractors/${brandKit.slug}`}
        robots="noindex, nofollow"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div
          className="relative py-20 px-4"
          style={{
            backgroundColor: brandKit.primary_color || '#7950a2',
            backgroundImage: brandKit.hero_image_url
              ? `url(${brandKit.hero_image_url})`
              : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative max-w-4xl mx-auto text-center text-white">
            {brandKit.logo_url && (
              <img
                src={brandKit.logo_url}
                alt={brandKit.company_name}
                className="h-24 w-auto mx-auto mb-6 bg-white/10 rounded-lg p-4"
              />
            )}
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{brandKit.company_name}</h1>
            {brandKit.tagline && <p className="text-xl text-white/90 mb-6">{brandKit.tagline}</p>}
            {brandKit.google_rating && (
              <div className="flex items-center justify-center gap-2">
                <Star className="size-5 fill-yellow-400 text-yellow-400" />
                <span className="text-lg font-semibold">{brandKit.google_rating.toFixed(1)}</span>
                <span className="text-white/80">Google Rating</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2
              className="text-2xl font-bold text-gray-900 mb-6"
              style={{ color: brandKit.secondary_color || undefined }}
            >
              Contact Information
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {brandKit.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="size-5 text-gray-400 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">{brandKit.address}</p>
                    {brandKit.city && brandKit.state && (
                      <p className="text-gray-600">
                        {brandKit.city}, {brandKit.state} {brandKit.zip}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {brandKit.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="size-5 text-gray-400 mt-1" />
                  <a
                    href={`tel:${brandKit.phone}`}
                    className="hover:underline"
                    style={{
                      color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                    }}
                  >
                    {brandKit.phone}
                  </a>
                </div>
              )}
              {brandKit.email && (
                <div className="flex items-start gap-3">
                  <Mail className="size-5 text-gray-400 mt-1" />
                  <a
                    href={`mailto:${brandKit.email}`}
                    className="hover:underline"
                    style={{
                      color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                    }}
                  >
                    {brandKit.email}
                  </a>
                </div>
              )}
              {brandKit.website && (
                <div className="flex items-start gap-3">
                  <Globe className="size-5 text-gray-400 mt-1" />
                  <a
                    href={brandKit.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{
                      color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                    }}
                  >
                    Visit Website
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* About Section */}
          {brandKit.about && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-4"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                About {brandKit.company_name}
              </h2>
              <div className="prose max-w-none text-gray-700">
                <p>{brandKit.about}</p>
              </div>
            </div>
          )}

          {/* Services */}
          {brandKit.services && brandKit.services.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Services
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {brandKit.services.map((service) => (
                  <div key={service.id} className="flex items-start gap-3">
                    <Building2
                      className="size-5 mt-1 flex-shrink-0"
                      style={{
                        color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                      }}
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{service.name}</h3>
                      {service.description && (
                        <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Areas */}
          {brandKit.service_areas && brandKit.service_areas.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Service Areas
              </h2>
              <div className="flex flex-wrap gap-2">
                {brandKit.service_areas.map((area) => (
                  <span
                    key={area.id}
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: brandKit.primary_color
                        ? `${brandKit.primary_color}20`
                        : '#eff6ff',
                      color: brandKit.primary_color || '#7950a2',
                    }}
                  >
                    {area.city}, {area.state} {area.zip}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {brandKit.reviews && brandKit.reviews.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Customer Reviews
              </h2>
              <div className="space-y-6">
                {brandKit.reviews.slice(0, 5).map((review) => (
                  <div
                    key={review.id}
                    className="border-b border-gray-200 pb-6 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`size-4 ${
                              i < review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold text-gray-900">{review.author_name}</span>
                      <span className="text-sm text-gray-500">
                        {new Date(review.review_date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {brandKit.certifications && brandKit.certifications.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Certifications & Licenses
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {brandKit.certifications.map((cert) => (
                  <div key={cert.id} className="flex items-start gap-3">
                    {cert.logo_url && (
                      <img src={cert.logo_url} alt={cert.name} className="size-12 object-contain" />
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{cert.name}</h3>
                      {cert.issuer && <p className="text-sm text-gray-600">{cert.issuer}</p>}
                      {cert.certification_number && (
                        <p className="text-xs text-gray-500 mt-1">#{cert.certification_number}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project Photos */}
          {brandKit.project_photos && brandKit.project_photos.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Project Photos
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {brandKit.project_photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img
                      src={photo.url}
                      alt={photo.caption || 'Project photo'}
                      className="w-full h-full object-cover"
                    />
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Social Links */}
          {brandKit.social_links && Object.values(brandKit.social_links).some(Boolean) && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2
                className="text-2xl font-bold text-gray-900 mb-6"
                style={{ color: brandKit.secondary_color || undefined }}
              >
                Follow Us
              </h2>
              <div className="flex flex-wrap gap-4">
                {brandKit.social_links.facebook && (
                  <a
                    href={brandKit.social_links.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Facebook
                  </a>
                )}
                {brandKit.social_links.instagram && (
                  <a
                    href={brandKit.social_links.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 text-sm font-medium"
                  >
                    Instagram
                  </a>
                )}
                {brandKit.social_links.twitter && (
                  <a
                    href={brandKit.social_links.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 text-sm font-medium"
                  >
                    Twitter / X
                  </a>
                )}
                {brandKit.social_links.linkedin && (
                  <a
                    href={brandKit.social_links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 text-sm font-medium"
                  >
                    LinkedIn
                  </a>
                )}
                {brandKit.social_links.youtube && (
                  <a
                    href={brandKit.social_links.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                  >
                    YouTube
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-200 mt-12 py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
            <p>
              &copy; {new Date().getFullYear()} {brandKit.company_name}. All rights reserved.
            </p>
            <p className="mt-2">
              <Link
                to="/contractors"
                className="hover:underline"
                style={{
                  color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                }}
              >
                View All Contractors
              </Link>
              {' • '}
              <a
                href={getAppLink('/register')}
                className="hover:underline"
                style={{
                  color: accessibleTextOnWhite(brandKit.primary_color, brandKit.accent_color),
                }}
              >
                Join Our Directory
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
